import { SelectOptionType } from '@octano/global-ui';

import { actionKitImpFunction } from '../../../../components/ozmoit/common';
import { baseToSelectOption } from '../../../academicOffers/courses/utils';
import {
  ActivityType,
  ProgramUnitSubject,
  Course,
  deleteStudyPlanRequest,
  getFormDataRequest,
  getCourseFormDataRequest,
  getProgramFormDataRequest,
  getStudyPlansListRequest,
  NonStructuredType,
  StudyPlanListResponse,
} from '../api';

export interface StudyPlansListParams {
  page: number;
  items_per_page: number;
  name?: string;
  code?: string;
  schoolId?: number;
  modalityId?: number;
  campusId?: number;
}

interface FormDataActionReturn {
  schools: SelectOptionType[];
  campuses: SelectOptionType[];
  modalities: SelectOptionType[];
  academicLevels: SelectOptionType[];
}

interface CourseFormDataActionReturn {
  courses: Course[];
  activityTypes: ActivityType[];
  academicLevels: SelectOptionType[];
}

interface ProgramFormDataActionReturn {
  schools: SelectOptionType[];
  modalities: SelectOptionType[];
  modalitiesDeliveryMode: Record<number, string>;
  activityTypes: ActivityType[];
  subjects: ProgramUnitSubject[];
  academicLevels: SelectOptionType[];
}

const getFormDataAction = actionKitImpFunction(
  async (): Promise<FormDataActionReturn> => {
    const response = await getFormDataRequest();
    if (response.error) {
      throw new Error('Error fetching form data');
    }

    const { data } = response;

    return {
      schools: baseToSelectOption(data?.schools),
      campuses: baseToSelectOption(data?.campuses),
      modalities: baseToSelectOption(data?.modalities),
      academicLevels: baseToSelectOption(data?.academicLevels),
    };
  },
);

const getCourseFormDataAction = actionKitImpFunction(
  async (): Promise<CourseFormDataActionReturn> => {
    const response = await getCourseFormDataRequest();
    if (response.error) {
      throw new Error('Error fetching course form data');
    }

    const { data } = response;

    return {
      courses: data?.courses || [],
      activityTypes: data?.activityTypes || [],
      academicLevels: baseToSelectOption(data?.academicLevels),
    };
  },
);

const getProgramFormDataAction = actionKitImpFunction(
  async (): Promise<ProgramFormDataActionReturn> => {
    const response = await getProgramFormDataRequest();
    if (response.error) {
      throw new Error('Error fetching program form data');
    }

    const { data } = response;

    return {
      schools: baseToSelectOption(data?.schools),
      modalities: baseToSelectOption(data?.modalities),
      modalitiesDeliveryMode: Object.fromEntries(
        (data?.modalities || []).map((m) => [m.id, m.deliveryMode]),
      ),
      activityTypes: data?.activityTypes || [],
      subjects: data?.subjects || [],
      academicLevels: baseToSelectOption(data?.academicLevels),
    };
  },
);

const getStudyPlansListAction = actionKitImpFunction(
  async (params: StudyPlansListParams): Promise<StudyPlanListResponse> => {
    const { page, items_per_page, ...filters } = params;
    const response = await getStudyPlansListRequest(
      filters,
      page,
      items_per_page,
    );
    if (response.error) {
      throw new Error('Error fetching study plans list');
    }
    return response.data!;
  },
);

export interface DeleteStudyPlanParams {
  id: number;
  type?: NonStructuredType;
}

const deleteStudyPlanAction = actionKitImpFunction(
  async ({ id, type }: DeleteStudyPlanParams): Promise<void> => {
    const response = await deleteStudyPlanRequest(id, type);
    if (response.error) {
      throw new Error('Error deleting study plan');
    }
  },
);

export {
  getFormDataAction,
  getCourseFormDataAction,
  getProgramFormDataAction,
  getStudyPlansListAction,
  deleteStudyPlanAction,
};
export type {
  FormDataActionReturn,
  CourseFormDataActionReturn,
  ProgramFormDataActionReturn,
};
